import { useEffect, useRef, useState } from "react";
import { FiCheckCircle, FiClock, FiXCircle } from "react-icons/fi";
import { useNavigate, useSearchParams } from "react-router-dom";
import { sendThankYouEmail } from "../../utils/sendThankYouEmail";

const Step = ({ done, pending, failed, dim, label }) => (
    <div className={`flex items-center gap-3 text-sm ${dim ? "opacity-40" : ""}`}>
        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${done ? "bg-[#1D9E75]" :
            pending ? "bg-[#EF9F27]" :
                failed ? "bg-[#E24B4A]" :
                    "bg-gray-300"
            }`} />
        <span className={`text-gray-600 ${pending ? "animate-pulse" : ""}`}>{label}</span>
    </div>
);

const STATUS_CONFIG = {
    CHECKING: {
        iconBg: "bg-gray-100",
        badge: "Verifying", badgeCls: "bg-gray-100 text-gray-500",
        title: "Checking your payment",
        sub: "Please wait while we confirm your transaction with the payment gateway.",
        steps: [
            { done: true, label: "Payment initiated" },
            { pending: true, label: "Awaiting gateway confirmation" },
            { dim: true, label: "Updating your account" },
        ],
    },
    SUCCESS: {
        iconBg: "bg-[#EAF3DE]",
        badge: "Payment confirmed", badgeCls: "bg-[#EAF3DE] text-[#3B6D11]",
        title: "Donation received",
        sub: "Thank you for your generosity. Your contribution has been recorded and will make a real difference.",
        steps: [
            { done: true, label: "Payment initiated" },
            { done: true, label: "Gateway confirmed" },
            { done: true, label: "Donation recorded" },
        ],
    },
    PENDING: {
        iconBg: "bg-[#FAEEDA]",
        badge: "Processing", badgeCls: "bg-[#FAEEDA] text-[#854F0B]",
        title: "Payment is processing",
        sub: "Your payment is being processed. This can take a few minutes. You can safely close this page — we'll update your account automatically.",
        steps: [
            { done: true, label: "Payment initiated" },
            { pending: true, label: "Awaiting bank confirmation" },
            { dim: true, label: "Updating your account" },
        ],
    },
    FAILED: {
        iconBg: "bg-[#FCEBEB]",
        badge: "Payment failed", badgeCls: "bg-[#FCEBEB] text-[#A32D2D]",
        title: "Something went wrong",
        sub: "Your payment could not be processed. No amount has been deducted. Please try again.",
        steps: [
            { done: true, label: "Payment initiated" },
            { failed: true, label: "Gateway declined transaction" },
        ],
    },
};

const PaymentStatus = () => {
    const [searchParams] = useSearchParams();

    const status =
        searchParams.get("status") || "FAILED";

    const orderId =
        searchParams.get("order_id") || "";

    const navigate = useNavigate();
    const emailSent = useRef(false); // guard: send email exactly once

    // ── Read user from localStorage 
    const getUser = () => {
        try {
            const raw = localStorage.getItem("ngo-afsa-data");
            return raw ? JSON.parse(raw) : null;
        } catch {
            return null;
        }
    };

    // ── Send thank-you email once on SUCCESS 
    const triggerThankYouEmail = async (user, amount) => {
        if (emailSent.current) return;
        emailSent.current = true;
        try {
            if (user?.name && user?.email) {
                await sendThankYouEmail(user.name, amount, user.email);
            }
        } catch (err) {
            console.error("Thank-you email failed:", err);
        }
    };

    useEffect(() => {
        const amount =
            searchParams.get("amount") || "";

        if (status === "SUCCESS") {
            const user = getUser();

            triggerThankYouEmail(user, amount);

            const timer = setTimeout(() => {
                navigate("/");
            }, 6000);

            return () => clearTimeout(timer);
        }
    }, [status, searchParams, navigate]);
    const cfg = STATUS_CONFIG[status] ?? STATUS_CONFIG.CHECKING;

    return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4 py-10">
            <div className="bg-white border border-gray-200 rounded-2xl p-8 sm:p-10 w-full max-w-md text-center shadow-sm">

                {/* Icon */}
                <div className={`w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center mx-auto mb-5 ${cfg.iconBg}`}>
                    {status === "CHECKING" && (
                        <div className="w-7 h-7 border-[2.5px] border-gray-200 border-t-[#1D9E75] rounded-full animate-spin" />
                    )}
                    {status === "SUCCESS" && <FiCheckCircle size={38} color="#3B6D11" />}
                    {status === "FAILED" && <FiXCircle size={38} color="#A32D2D" />}
                    {status === "PENDING" && <FiClock size={38} color="#854F0B" />}
                </div>

                {/* Badge */}
                <span className={`inline-block text-xs font-medium px-3 py-1 rounded-full mb-4 ${cfg.badgeCls}`}>
                    {cfg.badge}
                </span>

                {/* Title & subtitle */}
                <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-2">{cfg.title}</h1>
                <p className="text-sm sm:text-base text-gray-500 leading-relaxed mb-6">{cfg.sub}</p>

                {/* Progress steps */}
                <div className="flex flex-col gap-3 text-left mb-6 px-2">
                    {cfg.steps.map((step, i) => <Step key={i} {...step} />)}
                </div>

                {/* Order ID */}
                {orderId && (
                    <p className="text-xs text-gray-400 font-mono mb-6">Order ID: {orderId}</p>
                )}

                <hr className="border-gray-100 mb-6" />

                {/* Actions */}
                {status === "SUCCESS" && (
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        <button
                            onClick={() => navigate("/")}
                            className="px-6 py-2.5 rounded-lg bg-[#1D9E75] text-white text-sm font-medium hover:bg-[#0F6E56] transition-colors"
                        >
                            Back to home
                        </button>
                    </div>
                )}
                {status === "FAILED" && (
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">

                        <button
                            onClick={() => navigate("/")}
                            className="px-6 py-2.5 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-colors"
                        >
                            Go home
                        </button>
                    </div>
                )}
                {status === "PENDING" && (
                    <p className="text-xs text-gray-400">Rechecking automatically in a few seconds...</p>
                )}
                {status === "CHECKING" && (
                    <p className="text-xs text-gray-400 animate-pulse">This usually takes just a moment</p>
                )}
            </div>
        </div>
    );
};

export default PaymentStatus;